import * as React from "react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { Plus, MoreHorizontal } from "lucide-react";

const mobileTabsListVariants = cva(
  "h-10 p-1 bg-muted text-muted-foreground rounded-xl flex items-center gap-0 flex-1",
  {
    variants: {
      size: {
        sm: "h-9 p-0.5",
        md: "h-10 p-1",
        lg: "h-12 p-1.5",
      },
    },
    defaultVariants: {
      size: "md",
    },
  }
);

const mobileTabsTriggerVariants = cva(
  "h-8 px-6 text-sm font-medium rounded-lg flex items-center justify-center gap-1.5 transition-colors flex-1 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm",
  {
    variants: {
      size: {
        sm: "h-7 px-4 text-xs",
        md: "h-8 px-6 text-sm",
        lg: "h-10 px-8 text-base",
      },
    },
    defaultVariants: {
      size: "md",
    },
  }
);

const sideIconVariants = cva(
  "flex items-center justify-center rounded-lg bg-muted cursor-pointer hover:bg-accent transition-colors flex-shrink-0",
  {
    variants: {
      size: {
        sm: "w-9 h-9 p-2",
        md: "w-10 h-10 p-2.5",
        lg: "w-12 h-12 p-3",
      },
    },
    defaultVariants: {
      size: "md",
    },
  }
);

const getIconSize = (size: "sm" | "md" | "lg"): string => {
  switch (size) {
    case "sm":
      return "w-4 h-4";
    case "md":
      return "w-4 h-4";
    case "lg":
      return "w-5 h-5";
    default:
      return "w-4 h-4";
  }
};

export interface Tab<T extends string = string> {
  value: T;
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
}

export interface MobileTabSwitcherProps<T extends string = string>
  extends VariantProps<typeof mobileTabsListVariants> {
  tabs: Tab<T>[];
  value: T;
  onValueChange: (value: T) => void;
  className?: string;
  onLeftIconClick?: () => void;
  onRightIconClick?: () => void;
  showLeftIcon?: boolean;
  showRightIcon?: boolean;
  leftIcon?: React.ComponentType<{ className?: string }>;
  rightIcon?: React.ComponentType<{ className?: string }>;
}

/**
 * A mobile-optimized tab switcher built on shadcn/ui Tabs with icon and label support.
 * Features side icons (clock and more options) for additional actions.
 *
 * @example
 * ```tsx
 * const tabs: Tab<"chat" | "preview">[] = [
 *   { value: "chat", label: "Chat", icon: MessageCircle },
 *   { value: "preview", label: "Preview", icon: Eye },
 * ];
 *
 * <MobileTabSwitcher
 *   tabs={tabs}
 *   value={activeTab}
 *   onValueChange={setActiveTab}
 *   showLeftIcon={true}
 *   showRightIcon={true}
 *   onLeftIconClick={() => console.log("Clock clicked")}
 *   onRightIconClick={() => console.log("More clicked")}
 * />
 * ```
 */
export function MobileTabSwitcher<T extends string = string>({
  tabs,
  value,
  onValueChange,
  className,
  size = "md",
  onLeftIconClick,
  onRightIconClick,
  showLeftIcon = false,
  showRightIcon = false,
  leftIcon,
  rightIcon,
}: MobileTabSwitcherProps<T>): React.ReactElement | null {
  // Handle empty tabs array gracefully
  if (!tabs || tabs.length === 0) {
    return null;
  }

  return (
    <div className={cn("flex items-center gap-2 w-full max-w-md", className)}>
      {/* Left Icon - customizable via leftIcon prop, defaults to Plus */}
      {showLeftIcon && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onLeftIconClick}
          className={cn(sideIconVariants({ size }))}
          aria-label={leftIcon ? "Action" : "Add attachment"}
        >
          {leftIcon ? (
            React.createElement(leftIcon, {
              className: getIconSize(size ?? "md"),
            })
          ) : (
            <Plus className={getIconSize(size ?? "md")} />
          )}
        </Button>
      )}

      {/* Tabs */}
      <Tabs
        value={value}
        onValueChange={(val) => onValueChange(val as T)}
        className="flex-1"
      >
        <TabsList className={cn(mobileTabsListVariants({ size }))}>
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <TabsTrigger
                key={tab.value}
                value={tab.value}
                className={cn(mobileTabsTriggerVariants({ size }))}
              >
                {Icon && <Icon className={getIconSize(size ?? "md")} />}
                {tab.label}
              </TabsTrigger>
            );
          })}
        </TabsList>
      </Tabs>

      {/* Right Icon - More Options */}
      {showRightIcon && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onRightIconClick}
          className={cn(sideIconVariants({ size }))}
          aria-label={rightIcon ? "Close" : "More options"}
        >
          {rightIcon ? (
            React.createElement(rightIcon, {
              className: getIconSize(size ?? "md"),
            })
          ) : (
            <MoreHorizontal className={getIconSize(size ?? "md")} />
          )}
        </Button>
      )}
    </div>
  );
}
